import { ArrowLeft, FileText, Mail, MapPin } from 'lucide-react';
import { isRtlLocale } from '@/config/locales';
import { useLanguage } from '@/contexts/AppContext';
import {
  getLegalLandingPageTranslation,
  legalDocumentUpdatedAt,
} from '@/i18n/legalLandingPage';

const PRIVACY_EMAIL = 'privacidad@olulo.com.mx';

const LEGAL_DOCUMENTS = [
  {
    translationKey: 'privacyNotice',
    href: '/legal/aviso-privacidad.pdf',
  },
  {
    translationKey: 'termsAndConditions',
    href: '/legal/terminos-condiciones.pdf',
  },
  {
    translationKey: 'cookiePolicy',
    href: '/legal/politica-cookies.pdf',
  },
] as const;

interface LegalLandingPageProps {
  onBack: () => void;
}

export function LegalLandingPage({ onBack }: LegalLandingPageProps) {
  const { locale } = useLanguage();
  const t = getLegalLandingPageTranslation(locale);
  const updatedAt = legalDocumentUpdatedAt[locale];

  return (
    <main className="min-h-screen bg-muted text-foreground" dir={isRtlLocale(locale) ? 'rtl' : 'ltr'}>
      <header className="border-b border-border bg-background">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4">
          <button
            type="button"
            onClick={onBack}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground"
            aria-label={t.backAriaLabel}
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div>
            <p className="text-sm font-medium text-muted-foreground">{t.companyName}</p>
            <h1 className="text-xl font-semibold">{t.pageTitle}</h1>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-8">
        <section className="mb-8">
          <p className="mb-2 text-sm font-medium uppercase text-primary">{t.responsibleEyebrow}</p>
          <h2 className="mb-4 text-2xl font-semibold">{t.responsibleTitle}</h2>
          <dl className="grid gap-4 rounded-lg border border-border bg-background p-5 md:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t.legalNameLabel}</dt>
              <dd className="mt-1 font-semibold">{t.companyName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t.rfcLabel}</dt>
              <dd className="mt-1 font-semibold">OLU200905UA2</dd>
            </div>
            <div className="md:col-span-2">
              <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                {t.taxAddressLabel}
              </dt>
              <dd className="mt-1">
                Calle Principal Núm. 101, Interior 203-A, Colonia El Porvenir, C.P. 22224,
                Tijuana, Baja California, México
              </dd>
            </div>
            <div className="md:col-span-2">
              <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Mail className="h-4 w-4" aria-hidden="true" />
                {t.privacyEmailLabel}
              </dt>
              <dd className="mt-1">
                <a className="font-semibold text-primary underline" href={`mailto:${PRIVACY_EMAIL}`}>
                  {PRIVACY_EMAIL}
                </a>
              </dd>
            </div>
          </dl>
        </section>

        <section className="mb-8">
          <div className="mb-4 flex flex-col gap-1">
            <p className="text-sm font-medium uppercase text-primary">{t.publicDocumentsEyebrow}</p>
            <h2 className="text-2xl font-semibold">{t.legalDocumentsTitle}</h2>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {LEGAL_DOCUMENTS.map(document => (
              <a
                key={document.href}
                href={document.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-32 flex-col justify-between rounded-lg border border-border bg-background p-5 transition-colors hover:border-primary"
              >
                <span className="flex items-start gap-3">
                  <FileText className="mt-1 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  <span>
                    <span className="block font-semibold">{t.documents[document.translationKey]}</span>
                    <span className="mt-2 block text-sm text-muted-foreground">
                      {t.documentUpdatedLabel}: {updatedAt}
                    </span>
                  </span>
                </span>
                <span className="mt-4 text-sm font-semibold text-primary">{t.openPdf}</span>
              </a>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-background p-5">
          <p className="mb-2 text-sm font-medium uppercase text-primary">{t.arcoEyebrow}</p>
          <h2 className="mb-3 text-2xl font-semibold">{t.arcoTitle}</h2>
          <p className="mb-4 leading-relaxed">
            {t.arcoIntroBeforeEmail}{' '}
            <a className="font-semibold text-primary underline" href={`mailto:${PRIVACY_EMAIL}`}>
              {PRIVACY_EMAIL}
            </a>
            {t.arcoIntroAfterEmail}
          </p>
          <ul className="mb-4 list-disc space-y-2 pl-5">
            {t.arcoRequirements.map(requirement => (
              <li key={requirement}>{requirement}</li>
            ))}
          </ul>
          <p className="leading-relaxed">
            {t.responseBeforeInai}{' '}
            <a
              className="font-semibold text-primary underline"
              href="https://www.inai.org.mx/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.inaiLinkLabel}
            </a>
            {t.responseAfterInai}
          </p>
        </section>
      </div>
    </main>
  );
}
